/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/featureCategoriesApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API service functions for Feature Categories.
────────────────────────────────────────────────────────────────*/

import {
  requestFeatureCategoriesApi,
  FEATURE_CATEGORIES_ENDPOINT,
  FEATURE_CATEGORIES_AVAILABLE_FEATURES_ENDPOINT,
} from '../api';
import {
  FeatureCategory,
  FeatureCode,
  ListFeatureCategoriesParams,
  ListFeatureCategoriesResponse,
  CreateFeatureCategoryPayload,
  UpdateFeatureCategoryPayload,
} from '../types';

const QUICK_LIST_LIMIT = 100;

/**
 * Fetch Feature Categories with pagination and filters
 */
export async function fetchFeatureCategories(
  params: ListFeatureCategoriesParams,
  t: (key: string) => string
): Promise<ListFeatureCategoriesResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
    ...(params.search && { search: params.search }),
    ...(params.domainId != null && { domainId: params.domainId.toString() }),
  });

  const { data: rawData, pagination } = await requestFeatureCategoriesApi<FeatureCategory[]>(
    `${FEATURE_CATEGORIES_ENDPOINT}?${queryParams.toString()}`,
    t('featureCategories.messages.loadError')
  );

  return {
    success: true,
    data: Array.isArray(rawData) ? rawData : [],
    pagination: {
      page: pagination?.page ?? params.page,
      limit: pagination?.limit ?? params.limit,
      total: pagination?.total ?? 0,
      totalPages: pagination?.totalPages ?? 0,
    },
  };
}

/**
 * Fetch single Feature Category by ID
 */
export async function fetchFeatureCategoryById(
  id: string,
  t: (key: string) => string
): Promise<FeatureCategory> {
  const { data } = await requestFeatureCategoriesApi<FeatureCategory>(
    `${FEATURE_CATEGORIES_ENDPOINT}/${id}`,
    t('featureCategories.messages.loadError')
  );

  if (!data) {
    throw new Error(t('featureCategories.messages.loadError'));
  }

  return data;
}

/**
 * Fetch available Feature Codes
 */
export async function fetchAvailableFeatureCodes(
  t: (key: string) => string
): Promise<FeatureCode[]> {
  const { data } = await requestFeatureCategoriesApi<FeatureCode[]>(
    FEATURE_CATEGORIES_AVAILABLE_FEATURES_ENDPOINT,
    t('featureCategories.messages.loadError')
  );

  return Array.isArray(data) ? data : [];
}

/**
 * Create new Feature Category
 */
export async function createFeatureCategory(
  payload: CreateFeatureCategoryPayload,
  t: (key: string) => string
): Promise<{ success: boolean; data: FeatureCategory & { id?: number }; message?: string }> {
  const trimmedPayload = {
    name: payload.name.trim(),
    description: payload.description?.trim() || '',
    enabled: payload.enabled ?? true,
    featureCodeIds: payload.featureCodeIds,
  };

  const { data } = await requestFeatureCategoriesApi<Partial<FeatureCategory> & { id?: number }>(
    FEATURE_CATEGORIES_ENDPOINT,
    t('featureCategories.messages.createError'),
    {
      method: 'POST',
      body: JSON.stringify(trimmedPayload),
    }
  );

  return {
    success: true,
    data: (data || { ...trimmedPayload, id: data?.id }) as FeatureCategory & { id?: number },
  };
}

/**
 * Update Feature Category
 */
export async function updateFeatureCategory(
  id: string,
  payload: UpdateFeatureCategoryPayload,
  t: (key: string) => string
): Promise<{ success: boolean; data: FeatureCategory; message?: string }> {
  const trimmedPayload = {
    name: payload.name?.trim(),
    description: payload.description?.trim() || '',
    enabled: payload.enabled ?? true,
    featureCodeIds: payload.featureCodeIds || [],
  };

  const { data } = await requestFeatureCategoriesApi<FeatureCategory>(
    `${FEATURE_CATEGORIES_ENDPOINT}/${id}`,
    t('featureCategories.messages.updateError'),
    {
      method: 'PUT',
      body: JSON.stringify(trimmedPayload),
    }
  );

  if (!data) {
    throw new Error('Failed to update Feature Category - no data returned');
  }

  return {
    success: true,
    data: data,
  };
}

/**
 * Delete Feature Category
 */
export async function deleteFeatureCategory(
  id: string,
  t: (key: string) => string
): Promise<void> {
  await requestFeatureCategoriesApi<null>(
    `${FEATURE_CATEGORIES_ENDPOINT}/${id}`,
    t('featureCategories.messages.deleteError'),
    { method: 'DELETE' }
  );
}

/**
 * Fetch Feature Categories for Quick List
 */
export async function fetchFeatureCategoriesForQuickList(
  t: (key: string) => string
): Promise<FeatureCategory[]> {
  const queryParams = new URLSearchParams({
    page: '1',
    limit: QUICK_LIST_LIMIT.toString(),
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const { data } = await requestFeatureCategoriesApi<FeatureCategory[]>(
    `${FEATURE_CATEGORIES_ENDPOINT}?${queryParams.toString()}`,
    t('featureCategories.messages.loadError')
  );

  return Array.isArray(data) ? data : [];
}

